import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Grid2,
} from "@mui/material";
import BlogCard from "../../components/blog-card/blogCard";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import {
  collection,
  getDocs,
  QuerySnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "../../firestore-DB-config/firebase-config";
import { useLoader } from "../../components/loader/loader-context"; // Import the loader context

// Blog Interface
interface Blog {
  id: string;
  date: string;
  title: string;
  cardDescription: string;
  description: string;
  author: string;
  datePublished: string;
  blogImageUrl: string;
  authorImageUrl: string;
  slug: string;
}

const BlogPage: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]); // Store blog posts from Firestore
  const [loading, setLoading] = useState(true); // Loading state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const { showLoader, hideLoader } = useLoader(); // Destructure the loader functions

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const fetchBlogs = () => {
    showLoader();
    getDocs(collection(db, "blogs"))
      .then((querySnapshot: QuerySnapshot<DocumentData>) => {
        const blogList: Blog[] = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Firestore document ID
          ...doc.data(), // Spread other fields
        })) as Blog[];
        setBlogs(blogList);
        setLoading(false);
        hideLoader();
      })
      .catch((error) => {
        console.error("Error fetching blog posts: ", error);
        setLoading(false);
        hideLoader();
      }); 
  };

  useEffect(() => {
    fetchBlogs(); // Fetch blog posts on component mount
  }, []);

  // Filter blog posts based on search query and selected month
  const filteredPosts = blogs.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      blog.author.toLowerCase().includes(searchQuery.toLowerCase());

    // Extract the month from the datePublished string (e.g., "2024-10-03")
    const blogMonthNumber = new Date(blog.datePublished).getMonth() + 1; // Add 1 to match 1-based month index
    const blogMonth = months[blogMonthNumber - 1]; // Subtract 1 to match zero-based index in `months` array

    const matchesMonth = selectedMonth === "" || blogMonth === selectedMonth;

    return matchesSearch && matchesMonth;
  });

  return (
    <>
      <Header />
      <Container sx={{ my: { xs: 6, md: 8 }, px: { xs: 2, md: 4, lg: 0 } }}>
        {/* Header Section */}
        <Box my={4}>
          <Typography
            textAlign="left"
            fontWeight={700}
            color="rgba(1, 29, 51, 1)"
            gutterBottom
            sx={{ fontSize: { xs: "20px", sm: "28px" } }}
          >
            CHECKOUT OUR NEW
          </Typography>
          <Typography
            textAlign="left"
            fontWeight={540}
            mb={2}
            color="rgba(1, 29, 51, 1)"
            sx={{ fontSize: { xs: "24px", sm: "36px" } }}
          >
            LATEST BLOGS & POSTS
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#7A7A7A",
              marginBottom: "20px",
              fontSize: { xs: "14px", sm: "16px" },
            }}
          >
            Donec Porttitor Euismod Dignissim. Nullam A Lacinia Ipsum, Nec
            Dignissim Purus. Nulla Convallis Ipsum Molestie Nibh Malesuada.
          </Typography>
        </Box>

        {/* Search and Filter Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          my={4}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <TextField
            label="Search Here"
            variant="outlined"
            fullWidth
            sx={{
              maxWidth: { xs: "100%", sm: "65%" },
              borderRadius: "14px",
              "& .MuiOutlinedInput-root": { borderRadius: "14px" },
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <TextField
            select
            label="Select Month"
            variant="outlined"
            fullWidth
            sx={{
              maxWidth: { xs: "100%", sm: "30%" },
              borderRadius: "14px",
              mt: { xs: 2, sm: 0 },
              "& .MuiOutlinedInput-root": { borderRadius: "14px" },
            }}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <MenuItem value="">All Months</MenuItem>
            {months.map((month, index) => (
              <MenuItem key={index} value={month}>
                {month}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Blog Posts Section */}
        <Grid2
          container
          spacing={ {xs: 3, sm: 6} }
          justifyContent={{ xs: "center", md: "left" }}
          marginTop={5}
        >
          {loading ? (
            <Typography variant="h5" sx={{ my: 25 }}>
              Loading...
            </Typography>
          ) : filteredPosts.length > 0 ? (
            filteredPosts.map((blog) => (
              <Grid2
                size={{ xs: 9, md: 6, lg: 4}}
                key={blog.id}
              >
                <BlogCard blog={blog} />
              </Grid2>
            ))
          ) : (
            <Typography
              variant="h4"
              sx={{ color: "#7A7A7A", my: 25, fontWeight: "600" }}
            >
              No posts match your search or filter.
            </Typography>
          )}
        </Grid2>
      </Container>
      <Footer />
    </>
  );
};

export default BlogPage;
